import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { apiFetch } from '@/utils/api-fetch';

export interface ToolsUser {
  id: string;

  email: string;

  displayName: string | null;

  allowedSiteIds: string[] | null;

  isSuperAdmin: boolean;
}

interface AuthContextValue {
  user: ToolsUser | null;

  loading: boolean;

  requestLoginOtp: (email: string) => Promise<void>;

  verifyLoginOtp: (email: string, otp: string) => Promise<{ loginChallenge: string }>;

  login: (email: string, password: string, loginChallenge: string) => Promise<void>;

  logout: () => Promise<void>;

  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Keeps the server session alive while the tab is open (sliding renewal on the backend). */

const SESSION_KEEPALIVE_MS = 4 * 60 * 1000;

function normalizeToolsUser(raw: ToolsUser): ToolsUser {
  const allowedSiteIds = raw.allowedSiteIds ?? null;

  const isSuperAdmin =
    raw.isSuperAdmin === true || allowedSiteIds === null || allowedSiteIds === undefined;

  return {
    ...raw,

    allowedSiteIds,

    isSuperAdmin,
  };
}

async function readCurrentUser(): Promise<ToolsUser | null> {
  const response = await apiFetch('/auth/me');

  if (response.status === 401) {
    return null;
  }

  if (response.status === 429) {
    throw new Error('Too many requests. Please wait a minute and try again.');
  }

  if (!response.ok) {
    throw new Error('Could not verify your session. Please try again.');
  }

  const data = (await response.json()) as { user: ToolsUser };

  return normalizeToolsUser(data.user);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ToolsUser | null>(null);

  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const current = await readCurrentUser();

      setUser(current);
    } catch {
      // Transient network/server errors should not sign the user out.
    }
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const current = await readCurrentUser();

        if (active) {
          setUser(current);
        }
      } catch {
        // Keep loading finished; user stays null only when /auth/me returns 401.
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    const keepAlive = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };

    const intervalId = window.setInterval(keepAlive, SESSION_KEEPALIVE_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refresh();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);

      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, refresh]);

  const requestLoginOtp = useCallback(async (email: string) => {
    const response = await apiFetch('/auth/login/otp/request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    const data = (await response.json().catch(() => ({}))) as { error?: string };

    if (!response.ok) {
      throw new Error(data.error || 'Could not send a verification code. Please try again.');
    }
  }, []);

  const verifyLoginOtp = useCallback(async (email: string, otp: string) => {
    const response = await apiFetch('/auth/login/otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });

    const data = (await response.json().catch(() => ({}))) as {
      error?: string;
      loginChallenge?: string;
    };

    if (!response.ok || !data.loginChallenge) {
      throw new Error(data.error || 'That code did not work. Please try again.');
    }

    return { loginChallenge: data.loginChallenge };
  }, []);

  const login = useCallback(async (email: string, password: string, loginChallenge: string) => {
    const response = await apiFetch('/auth/login', {
      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ email, password, loginChallenge }),
    });

    const data = (await response.json().catch(() => ({}))) as {
      error?: string;

      user?: ToolsUser;
    };

    if (!response.ok) {
      throw new Error(data.error || 'Sign in failed. Please check your email and password.');
    }

    setUser(data.user ? normalizeToolsUser(data.user) : null);
  }, []);

  const logout = useCallback(async () => {
    await apiFetch('/auth/logout', { method: 'POST' });

    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, requestLoginOtp, verifyLoginOtp, login, logout, refresh }),

    [user, loading, requestLoginOtp, verifyLoginOtp, login, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return ctx;
}
